import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import api, { getErrorMessage } from '../lib/api';
import { Loader2 } from 'lucide-react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
    const navigate = useNavigate();
    const location = useLocation();
    const { loginWithGoogle } = useAuth();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            try {
                const hash = location.hash;
                const params = new URLSearchParams(hash.replace('#', ''));
                const sessionId = params.get('session_id');

                if (!sessionId) {
                    toast.error('Sessão inválida');
                    navigate('/login', { replace: true });
                    return;
                }

                const response = await api.post('/auth/google/callback', {
                    session_id: sessionId
                });

                const { token, user } = response.data;

                if (!token || !user) {
                    toast.error('Resposta de autenticação inválida');
                    navigate('/login', { replace: true });
                    return;
                }

                // Save token and user to auth context + localStorage
                let finalUser = loginWithGoogle(token, user);

                // If new user without profile, activate as tutor first
                if (!finalUser?.user_type || finalUser.user_type === 'pending' || finalUser.user_type === null) {
                    try {
                        const activateRes = await api.post('/auth/activate-tutor', {}, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (activateRes.data?.user) {
                            const newToken = activateRes.data.token || token;
                            finalUser = loginWithGoogle(newToken, activateRes.data.user);
                        }
                    } catch (e) {
                        // If activation fails, user still has their Google session
                    }
                }

                // Check if profile needs completion (no phone, no password, or not completed)
                const needsCompletion = !finalUser?.phone || finalUser.phone === '' || 
                    finalUser?.profile_completed === false || !finalUser?.address;
                
                if (needsCompletion) {
                    // Enviar para o funil COMPLETO de registro
                    navigate('/register?from_google=true', { 
                        replace: true,
                        state: { 
                            fromGoogle: true, 
                            googleUser: { 
                                name: finalUser?.name || '', 
                                email: finalUser?.email || '',
                                profile_photo: finalUser?.profile_photo || ''
                            }
                        }
                    });
                    return;
                }

                // Check if phone needs verification
                if (!finalUser?.phone_verified) {
                    toast.success('Verifique seu telefone para continuar');
                    navigate('/verification', { replace: true });
                    return;
                }

                toast.success('Bem-vindo ao RecepPet!');

                // Determine redirect based on user type
                const userType = finalUser?.user_type;
                const userEmail = finalUser?.email?.toLowerCase();
                
                let redirectTo = '/tutor'; // Default for Google users
                if (userEmail === 'admin@receppet.com.br' || userType === 'admin') {
                    redirectTo = '/admin';
                } else if (['host', 'caregiver', 'driver', 'partner', 'provider'].includes(userType)) {
                    if (finalUser?.is_also_tutor || finalUser?.is_dual_profile) {
                        redirectTo = '/tutor';
                    } else {
                        redirectTo = '/provider';
                    }
                }

                // Force navigation with replace to avoid back-button loops
                navigate(redirectTo, { replace: true });
            } catch (error) {
                toast.error(getErrorMessage(error, 'Erro ao autenticar com Google'));
                navigate('/login', { replace: true });
            }
        };

        processAuth();
    }, [location, navigate, loginWithGoogle]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]" data-testid="auth-callback-page">
            <div className="text-center">
                <Loader2 className="w-12 h-12 text-[#4A90B2] animate-spin mx-auto mb-4" />
                <p className="text-slate-600">Autenticando...</p>
            </div>
        </div>
    );
}
